import React, { useEffect, useState, useMemo } from 'react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useTranslation } from '../context/LanguageContext';

export default function WorkoutList({ userId, role }) {
  const { t, lang } = useTranslation();
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());

  // Search & Filter State
  const [searchQuery, setSearchQuery] = useState("");
  const [periodFilter, setPeriodFilter] = useState("ALL"); // 'ALL' | 'THIS_MONTH' | '30_DAYS'
  const [originFilter, setOriginFilter] = useState("ALL"); // 'ALL' | 'coach' | 'standard' | 'freestyle' | 'personal'

  useEffect(() => {
    const handleLocalSave = (e) => {
      if (!e.detail.userId || e.detail.userId === userId || userId === 'all') {
        setWorkouts(prev => [e.detail, ...prev]);
      }
    };
    window.addEventListener('local_workout_saved', handleLocalSave);
    return () => window.removeEventListener('local_workout_saved', handleLocalSave);
  }, [userId]);

  useEffect(() => {
    let unsubscribe;
    try {
      const q = query(
        collection(db, 'workouts'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      unsubscribe = onSnapshot(q, (snapshot) => {
        const docs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setWorkouts(docs);
      }, (error) => {
        console.warn("Offline workouts listener notice:", error.message);
      });
    } catch (error) {
      console.warn("Firestore workouts init warning:", error?.message);
    }
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [userId]);

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleRepeat = (w) => {
    window.dispatchEvent(new CustomEvent('repeat_past_workout', { detail: w }));
  };

  // Helper for origin category detection
  const getWorkoutCategory = (w) => {
    const rawSource = w.originalOriginSource || w.originSource;
    if (rawSource === 'coach') return 'coach';
    if (rawSource === 'standard') return 'standard';
    if (rawSource === 'freestyle') return 'freestyle';
    if (rawSource === 'personal') return 'personal';

    if (w.trainerId && w.trainerId !== "global" && w.trainerId !== "standard") return 'coach';
    if (w.isGlobal || (w.planTitle && !w.planTitle.includes("Własny") && !w.planTitle.includes("Spontaniczny") && !w.planTitle.includes("Powtórzenie") && !w.planTitle.includes("Duplikat"))) return 'standard';
    if (!w.planTitle && (!w.routineTitle || w.routineTitle.includes("Pusta") || w.routineTitle.includes("Blank") || w.routineTitle.includes("Swobodny") || w.routineTitle.includes("Freestyle"))) return 'freestyle';
    return 'personal';
  };

  // Filtering Logic
  const filteredWorkouts = useMemo(() => {
    return workouts.filter(w => {
      // Search query filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const titleMatch = (w.routineTitle || w.planTitle || '').toLowerCase().includes(q);
        const exMatch = (w.exercises || []).some(ex => (ex.name || '').toLowerCase().includes(q));
        if (!titleMatch && !exMatch) return false;
      }

      // Origin filter
      if (originFilter !== 'ALL') {
        const cat = getWorkoutCategory(w);
        if (cat !== originFilter) return false;
      }

      // Period filter
      if (periodFilter === 'THIS_MONTH') {
        const now = new Date();
        const wDate = w.createdAt ? (typeof w.createdAt === 'number' ? new Date(w.createdAt) : new Date(w.createdAt)) : (w.date ? new Date(w.date) : new Date());
        if (wDate.getMonth() !== now.getMonth() || wDate.getFullYear() !== now.getFullYear()) return false;
      } else if (periodFilter === '30_DAYS') {
        const cutoff = Date.now() - (30 * 24 * 60 * 60 * 1000);
        const wTime = w.createdAt ? (typeof w.createdAt === 'number' ? w.createdAt : new Date(w.createdAt).getTime()) : (w.date ? new Date(w.date).getTime() : Date.now());
        if (wTime < cutoff) return false;
      }

      return true;
    });
  }, [workouts, searchQuery, periodFilter, originFilter]);

  // Group Workouts Chronologically by Month
  const groupedWorkouts = useMemo(() => {
    const groups = {};
    filteredWorkouts.forEach(w => {
      const dObj = w.createdAt ? (typeof w.createdAt === 'number' ? new Date(w.createdAt) : new Date(w.createdAt)) : (w.date ? new Date(w.date) : new Date());
      const monthYearKey = isNaN(dObj.getTime())
        ? (lang === 'pl' ? 'Ostatnie Treningi' : 'Recent Workouts')
        : dObj.toLocaleDateString(lang === 'pl' ? 'pl-PL' : 'en-US', { month: 'long', year: 'numeric' });

      if (!groups[monthYearKey]) groups[monthYearKey] = [];
      groups[monthYearKey].push(w);
    });
    return groups;
  }, [filteredWorkouts, lang]);

  // Relative Date Helper
  const getRelativeDateStr = (dateStr, createdAt) => {
    const dObj = createdAt ? (typeof createdAt === 'number' ? new Date(createdAt) : new Date(createdAt)) : (dateStr ? new Date(dateStr) : new Date());
    if (isNaN(dObj.getTime())) return dateStr || 'N/A';
    
    const diffDays = Math.floor(Math.abs(new Date() - dObj) / (1000 * 60 * 60 * 24));
    const formatted = dObj.toLocaleDateString(lang === 'pl' ? 'pl-PL' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric' });

    if (diffDays === 0) return `${lang === 'pl' ? 'Dzisiaj' : 'Today'} • ${formatted}`;
    if (diffDays === 1) return `${lang === 'pl' ? 'Wczoraj' : 'Yesterday'} • ${formatted}`;
    if (diffDays < 7) return `${lang === 'pl' ? `${diffDays} dni temu` : `${diffDays} days ago`} • ${formatted}`;
    return formatted;
  };

  if (loading) return <div className="p-6 text-slate-400 font-bold">{lang === 'pl' ? "Ładowanie historii..." : "Loading history logs..."}</div>;

  return (
    <div className="mt-6 pt-6 border-t border-slate-800 space-y-6">
      
      {/* 1. HEADER & FILTER BAR */}
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-xl font-extrabold text-white m-0 flex items-center gap-2">
              <span className="material-symbols-outlined text-2xl text-emerald-400">history</span>
              <span>{t('liveTracker.loggedHistory', 'Logged Workout History & Vault')}</span>
            </h3>
            <p className="text-xs text-slate-400 m-0 font-medium pt-1">
              {lang === 'pl' 
                ? "Przeglądaj ukończone sesje, rekordy życiowe i objętość treningową." 
                : "Review past sessions, personal records, and training volume."}
            </p>
          </div>

          <span className="bg-slate-900 border border-slate-800 text-emerald-400 font-extrabold text-xs px-3.5 py-1.5 rounded-full shadow-inner">
            {filteredWorkouts.length} {lang === 'pl' ? 'Ukończonych Sesji' : 'Workouts Logged'}
          </span>
        </div>

        {/* Search Input & Filter Chips Bar */}
        <div className="space-y-3 bg-slate-900/80 border border-slate-800 p-4 rounded-2xl shadow-xl">
          <div className="relative">
            <span className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-base">search</span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={lang === 'pl' ? "Szukaj w historii po nazwie lub ćwiczeniu..." : "Search history by workout name or exercise..."}
              className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500/60 text-xs text-white rounded-xl pl-10 pr-4 py-3 outline-none transition shadow-inner"
            />
          </div>

          <div className="flex items-center justify-between flex-wrap gap-3">
            {/* Period Filters */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar">
              {[
                { id: 'ALL', labelPL: 'Całość', labelEN: 'All Time' },
                { id: 'THIS_MONTH', labelPL: 'Ten miesiąc', labelEN: 'This Month' },
                { id: '30_DAYS', labelPL: 'Ostatnie 30 dni', labelEN: 'Last 30 Days' }
              ].map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPeriodFilter(p.id)}
                  className={`px-3 py-1.5 rounded-xl text-[11px] font-extrabold transition cursor-pointer shrink-0 border ${
                    periodFilter === p.id
                      ? 'bg-emerald-600 text-white border-emerald-400 shadow-md'
                      : 'bg-slate-950 text-slate-400 border-slate-800 hover:border-slate-700'
                  }`}
                >
                  {lang === 'pl' ? p.labelPL : p.labelEN}
                </button>
              ))}
            </div>

            {/* Origin Category Filter Pills */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar">
              {[
                { id: 'ALL', labelPL: 'Wszystkie', labelEN: 'All' },
                { id: 'coach', labelPL: 'Od Trenera', labelEN: 'Coach Plans' },
                { id: 'standard', labelPL: 'Platforma', labelEN: 'Standard' },
                { id: 'freestyle', labelPL: 'Swobodne', labelEN: 'Freestyle' },
                { id: 'personal', labelPL: 'Własne', labelEN: 'Personal' }
              ].map(o => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setOriginFilter(o.id)}
                  className={`px-3 py-1.5 rounded-xl text-[11px] font-extrabold transition cursor-pointer shrink-0 border ${
                    originFilter === o.id
                      ? 'bg-sky-600 text-white border-sky-400 shadow-md'
                      : 'bg-slate-950 text-slate-400 border-slate-800 hover:border-slate-700'
                  }`}
                >
                  {lang === 'pl' ? o.labelPL : o.labelEN}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 2. CHRONOLOGICAL LOGBOOK WORKOUT FEED */}
      {filteredWorkouts.length === 0 ? (
        <div className="p-8 text-center bg-slate-900/60 border border-dashed border-slate-800 rounded-3xl space-y-3">
          <span className="material-symbols-outlined text-slate-600 text-4xl">history</span>
          <p className="m-0 text-sm font-semibold text-slate-400">
            {lang === 'pl' ? "Brak zarejestrowanych sesji treningowych spełniających kryteria." : "No logged workout sessions found matching your filters."}
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(groupedWorkouts).map(([monthYear, workoutGroup]) => (
            <div key={monthYear} className="space-y-4">
              
              {/* Month Divider Header */}
              <div className="flex items-center gap-3 border-b border-slate-800 pb-2">
                <span className="material-symbols-outlined text-base text-emerald-400">calendar_month</span>
                <h4 className="text-sm font-black text-white capitalize m-0 tracking-wide">
                  {monthYear}
                </h4>
                <span className="text-xs font-bold text-slate-500 bg-slate-900 border border-slate-800 px-2.5 py-0.5 rounded-full">
                  {workoutGroup.length} {lang === 'pl' ? (workoutGroup.length === 1 ? 'Trening' : 'Treningi') : (workoutGroup.length === 1 ? 'Session' : 'Sessions')}
                </span>
              </div>

              {/* Workout Cards in this Month Group */}
              <div className="space-y-3.5">
                {workoutGroup.map(workout => {
                  const isExpanded = expandedIds.has(workout.id);
                  const title = workout.routineTitle || workout.planTitle || (lang === 'pl' ? "Trening Własny" : "Custom Session");
                  const relDateStr = getRelativeDateStr(workout.date, workout.createdAt);
                  const category = getWorkoutCategory(workout);

                  const durationMin = workout.durationSeconds ? Math.round(workout.durationSeconds / 60) : 45;
                  const exercises = workout.exercises || [];
                  const volumeKg = workout.totalVolumeLifted || exercises.reduce((acc, ex) => {
                    return acc + (ex.sets || []).reduce((sAcc, set) => sAcc + ((Number(set.weight) || 0) * (Number(set.reps) || 0)), 0);
                  }, 0);
                  const setsCount = workout.completedSetsCount || exercises.reduce((acc, ex) => acc + (ex.sets || []).length, 0);

                  // Source badge config
                  let badgeClass = "bg-violet-500/10 text-violet-300 border-violet-500/30";
                  let badgeIcon = "star";
                  let badgeLabel = lang === 'pl' ? "WŁASNY" : "PERSONAL";

                  if (category === 'coach') {
                    badgeClass = "bg-emerald-500/10 text-emerald-300 border-emerald-500/30";
                    badgeIcon = "assignment_ind";
                    badgeLabel = lang === 'pl' ? "TRENER" : "COACH";
                  } else if (category === 'standard') {
                    badgeClass = "bg-cyan-500/10 text-cyan-300 border-cyan-500/30";
                    badgeIcon = "local_library";
                    badgeLabel = lang === 'pl' ? "STANDARD" : "STANDARD";
                  } else if (category === 'freestyle') {
                    badgeClass = "bg-amber-500/10 text-amber-300 border-amber-500/30";
                    badgeIcon = "edit_note";
                    badgeLabel = lang === 'pl' ? "FREESTYLE" : "FREESTYLE";
                  }

                  return (
                    <div 
                      key={workout.id}
                      className="bg-slate-900 border border-slate-800 hover:border-slate-700/90 rounded-2xl overflow-hidden shadow-xl transition-all duration-200"
                    >
                      {/* COLLAPSED SUMMARY HEADER BAR */}
                      <div 
                        onClick={() => toggleExpand(workout.id)}
                        className="p-4 md:p-5 cursor-pointer flex flex-col md:flex-row md:items-center justify-between gap-4 select-none hover:bg-slate-850/50 transition-colors"
                      >
                        {/* Left: Title, Date, Source Badge */}
                        <div className="space-y-1.5 min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[10px] font-black px-2.5 py-0.5 rounded-full inline-flex items-center gap-1 border ${badgeClass}`}>
                              <span className="material-symbols-outlined text-[12px]">{badgeIcon}</span>
                              <span>{badgeLabel}</span>
                            </span>

                            <span className="text-xs font-bold text-slate-400">
                              {relDateStr}
                            </span>
                          </div>

                          <h4 className="text-base font-extrabold text-white m-0 tracking-tight truncate">
                            {title}
                          </h4>
                        </div>

                        {/* Center / Right: Telemetry Chips & Action Buttons */}
                        <div className="flex items-center gap-2 flex-wrap shrink-0 justify-between md:justify-end">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="bg-slate-950 border border-slate-800 px-2.5 py-1 rounded-xl text-xs font-bold text-slate-300 flex items-center gap-1">
                              <span className="material-symbols-outlined text-xs text-amber-400">schedule</span>
                              <span>{durationMin} min</span>
                            </span>

                            <span className="bg-slate-950 border border-slate-800 px-2.5 py-1 rounded-xl text-xs font-bold text-emerald-400 flex items-center gap-1">
                              <span className="material-symbols-outlined text-xs text-emerald-400">fitness_center</span>
                              <span>{volumeKg.toLocaleString()} kg</span>
                            </span>

                            <span className="bg-slate-950 border border-slate-800 px-2.5 py-1 rounded-xl text-xs font-bold text-slate-300 flex items-center gap-1">
                              <span className="material-symbols-outlined text-xs text-indigo-400">task_alt</span>
                              <span>{setsCount} {lang === 'pl' ? 'serii' : 'sets'} • {exercises.length} {lang === 'pl' ? 'ćw.' : 'ex.'}</span>
                            </span>

                            {workout.caloriesBurned > 0 && (
                              <span className="bg-slate-950 border border-slate-800 px-2.5 py-1 rounded-xl text-xs font-bold text-amber-300 flex items-center gap-1">
                                <span className="material-symbols-outlined text-xs text-amber-400">local_fire_department</span>
                                <span>{workout.caloriesBurned} kcal</span>
                              </span>
                            )}

                            {workout.rpe && (
                              <span className="bg-slate-950 border border-slate-800 px-2.5 py-1 rounded-xl text-xs font-extrabold text-sky-300">
                                RPE {workout.rpe}
                              </span>
                            )}
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            {role === 'client' && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRepeat(workout);
                                }}
                                className="bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs px-3.5 py-2 rounded-xl transition cursor-pointer flex items-center gap-1 shadow-md active:scale-95 border border-emerald-500/30"
                                title={lang === 'pl' ? 'Powtórz ten trening' : 'Repeat workout'}
                              >
                                <span className="material-symbols-outlined text-sm">replay</span>
                                <span className="hidden sm:inline">{lang === 'pl' ? 'Powtórz' : 'Repeat'}</span>
                              </button>
                            )}

                            <span className="material-symbols-outlined text-slate-400 text-lg transition-transform">
                              {isExpanded ? 'expand_less' : 'expand_more'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* 3. EXPANDED SET-BY-SET BREAKDOWN */}
                      {isExpanded && (
                        <div className="p-4 md:p-5 bg-slate-950 border-t border-slate-800 space-y-4 animate-fade-in">
                          
                          {/* Notes Block */}
                          {workout.notes && (
                            <div className="bg-slate-900 border border-indigo-500/30 rounded-xl p-3.5 space-y-1">
                              <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest block flex items-center gap-1">
                                <span className="material-symbols-outlined text-xs">chat</span>
                                <span>{lang === 'pl' ? 'Uwagi i odczucia z treningu:' : 'Session Notes & Feedback:'}</span>
                              </span>
                              <p className="text-xs text-slate-200 italic m-0 font-medium">"{workout.notes}"</p>
                            </div>
                          )}

                          {/* Exercise List */}
                          <div className="space-y-4">
                            {exercises.map((ex, exIdx) => {
                              const sets = ex.sets || [];
                              return (
                                <div key={exIdx} className="bg-slate-900 border border-slate-800/90 rounded-xl p-4 space-y-3">
                                  <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <div className="flex items-center gap-2">
                                      <span className="w-6 h-6 rounded-lg bg-slate-950 border border-slate-800 text-emerald-400 font-mono text-xs font-bold flex items-center justify-center">
                                        {exIdx + 1}
                                      </span>
                                      <h5 className="text-sm font-extrabold text-white m-0">
                                        {ex.name}
                                      </h5>
                                      {ex.bodyPart && (
                                        <span className="text-[10px] text-slate-400 font-medium bg-slate-950 px-2 py-0.5 rounded-md border border-slate-800">
                                          {ex.bodyPart}
                                        </span>
                                      )}
                                    </div>

                                    <span className="text-xs font-bold text-slate-400">
                                      {sets.length} {lang === 'pl' ? 'serii' : 'sets'}
                                    </span>
                                  </div>

                                  {/* Sets Grid / Table */}
                                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                                    {sets.map((s, sIdx) => {
                                      const w = Number(s.weight) || 0;
                                      const r = Number(s.reps) || 0;
                                      const est1RM = w > 0 && r > 0 ? Math.round(w * (1 + r / 30)) : null;

                                      return (
                                        <div key={sIdx} className="bg-slate-950 border border-slate-800/80 p-2.5 rounded-xl text-center space-y-1">
                                          <div className="flex items-center justify-between text-[10px] text-slate-500 font-extrabold uppercase">
                                            <span>#{sIdx + 1}</span>
                                            <span className={s.type === 'W' ? 'text-amber-400' : s.type === 'D' ? 'text-purple-400' : 'text-slate-400'}>
                                              {s.type === 'W' ? (lang === 'pl' ? 'Rozgrz.' : 'Warmup') : s.type === 'D' ? 'Drop' : (lang === 'pl' ? 'Robocza' : 'Work')}
                                            </span>
                                          </div>
                                          <div className="text-xs text-white font-extrabold">
                                            <strong className="text-emerald-300 font-mono text-sm">{w} kg</strong> × <strong className="text-white font-mono text-sm">{r}</strong>
                                          </div>
                                          {est1RM && (
                                            <span className="text-[9px] text-slate-400 font-medium block">
                                              1RM: ~{est1RM}kg
                                            </span>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>

                                  {ex.remarks && (
                                    <p className="text-xs text-slate-300 italic m-0 pt-1 border-t border-slate-850">
                                      <span className="font-bold text-slate-400">{lang === 'pl' ? 'Uwaga: ' : 'Note: '}</span>"{ex.remarks}"
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

            </div>
          ))}
        </div>
      )}

    </div>
  );
}
